// Renders a DYMO label XML to a PNG data URL using the DYMO Connect web
// service (dymo.label.framework). The framework is only present on machines
// running DYMO Connect — the same requirement as DYMO printing (see
// hooks/use-dymo-sdk.ts for the app's detection/typing of the global).
// PHI-SAFE: callers pass XML already filled with synthetic sample data only
// (see sample-data.ts) — never live patient data. No remote service is
// involved for DYMO; rendering happens entirely via the local DYMO Connect
// process. (Ports legacy `Alpha-App/src/util/labelPreview/dymoPreview.js`.)

export type DymoPreviewErrorCode = 'DYMO_NOT_AVAILABLE'

export class DymoPreviewError extends Error {
  constructor(
    message: string,
    public readonly code: DymoPreviewErrorCode,
  ) {
    super(message)
    this.name = 'DymoPreviewError'
  }
}

interface DymoLabel {
  render(): string
}

interface DymoRenderFramework {
  openLabelXml(xml: string): DymoLabel
}

// Same `window.dymo?.label?.framework` path as hooks/use-dymo-sdk.ts's global
// detection, but widened to also cover `.render()` (the DYMO Connect SDK's
// real `openLabelXml()` result supports both `.print()` and `.render()`; the
// hook's `DymoFramework` type only declares the subset it uses).
function getFramework(): DymoRenderFramework | undefined {
  if (typeof window === 'undefined') return undefined
  return (window as unknown as { dymo?: { label?: { framework?: DymoRenderFramework } } }).dymo?.label?.framework
}

/**
 * Renders DYMO label XML to a `data:image/png;base64,...` URL via the local
 * DYMO Connect SDK. Throws `DymoPreviewError` (code `DYMO_NOT_AVAILABLE`)
 * when DYMO Connect is not running/installed on this machine.
 */
export async function renderDymoPreview(xml: string): Promise<string> {
  const fw = getFramework()
  if (!fw || typeof fw.openLabelXml !== 'function') {
    throw new DymoPreviewError('DYMO Connect is not available', 'DYMO_NOT_AVAILABLE')
  }
  const label = fw.openLabelXml(xml)
  const png = label.render()
  return `data:image/png;base64,${png}`
}
